/**
 * 水属性カードのトリガー実装
 *
 * このファイルには水属性カードの個別トリガー効果を実装します。
 * 各カードは独自のトリガー定義を持ち、triggerEngineによって管理されます。
 */

import { applyTemporaryAttackReduction } from '../temporaryAttack';
import { TRIGGER_TYPES, ACTIVATION_TYPES, TRIGGER_PRIORITIES } from '../triggerTypes';
import type { TriggerContext } from '../triggerEngine';
import type { CardWithId, MonsterInstance } from '../../types/card';
import type { TriggerDefinition } from '../../types/effect';
import {
  getPlayerContext,
  millDeck,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  drawCards,
  healLife,
  destroyMonster,
  modifyAttack,
  modifyHP,
  selectAndApplyStatusToOpponent,
  applyStatusToAllOpponentMonsters,
} from '../effectHelpers';
import { STATUS_EFFECT_TYPES, statusEffectEngine } from '../statusEffects';
import {
  getKonketsuEffect,
  findLinkableTargets,
  executeKonketsuLink,
  executeShinsyokuEffect,
  calculateShihenNoKibaDamage,
} from '../keywordAbilities';
import { getDepthBonus } from '../deepSeaZone';

/**
 * 水属性カードのトリガー定義
 * カードID => トリガー配列のマッピング
 */
export const waterCardTriggers = {
  /**
   * C0000039: アクア・メイデン
   * 【召喚時】相手モンスター1体を「眠り」（次のターン終了時まで行動不能、効果無効、ターン開始フェイズに50％で解除）にする。
   */
  C0000039: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体を「眠り」状態に',
      effect: (context: TriggerContext) => {
        // 眠り: 次のターン終了時まで行動不能＋効果無効、ターン開始時50%解除
        selectAndApplyStatusToOpponent(context as any,
          STATUS_EFFECT_TYPES.SLEEP,
          {
            duration: 2, // 次のターン終了時まで
            removeChance: 0.5, // 50%で解除
          },
          'アクア・メイデン'
        );
      },
    },
  ],

  /**
   * C0000041: ダーク・リヴァイアサン
   * 【召喚時】相手の場にいるモンスター全てに3000ダメージを与え、相手プレイヤーに破壊したモンスター1体につき800ダメージを与える。
   * このターン中、自分はダイレクトアタックできない。
   */
  C0000041: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手全体に3000、破壊数×800、ダイレクト禁止',
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer } = context;
        const { setOpponentField } = getPlayerContext(context);
        let destroyedCount = 0;

        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              const newHp = monster.currentHp - 3000;
              if (newHp <= 0) {
                destroyedCount++;
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              } else {
                return { ...monster, currentHp: newHp };
              }
            }
            return monster;
          });
        });

        if (destroyedCount > 0) {
          const totalDamage = destroyedCount * 800;
          addLog(`ダーク・リヴァイアサンの効果: ${destroyedCount}体破壊、相手に${totalDamage}ダメージ`, 'damage');
          conditionalDamage(context as any, totalDamage, 'opponent');
        }

        // このターン中、自分はダイレクトアタックできない
        const setMyStatusEffects = currentPlayer === 1
          ? (context as any).setP1StatusEffects
          : (context as any).setP2StatusEffects;
        const myStatusEffects = currentPlayer === 1
          ? (context as any).p1StatusEffects
          : (context as any).p2StatusEffects;

        if (setMyStatusEffects) {
          const result = statusEffectEngine.applyPlayerStatus(
            myStatusEffects || [],
            STATUS_EFFECT_TYPES.NO_DIRECT_ATTACK,
            {
              source: 'C0000041',
              sourceName: 'ダーク・リヴァイアサン',
            }
          );
          if (result.success && result.statusEffect) {
            setMyStatusEffects((prev: any) => [...(prev || []), result.statusEffect]);
          }
        }
        addLog('このターン中、ダイレクトアタックできない！', 'info');
      },
    },
  ],

  /**
   * C0000042: 潮の乙女
   * 【召喚時】手札の水属性カード1枚のSPコストを1軽減。
   */
  C0000042: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 手札の水属性カード1枚のコスト-1',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingHandSelection } = context;
        const { myHand, setMyHand } = getPlayerContext(context);

        // 水属性カードをフィルタ（自分自身以外）
        const waterCards = myHand.filter(c => c.attribute === '水');

        if (waterCards.length === 0) {
          addLog('潮の乙女の効果: 手札に水属性カードがありません', 'info');
          return;
        }

        // 1枚のみの場合は自動選択
        if (waterCards.length === 1) {
          const targetCard = waterCards[0];
          setMyHand(prev => prev.map(c =>
            c.uniqueId === targetCard.uniqueId
              ? { ...c, tempCostModifier: (c.tempCostModifier || 0) - 1, tempCostModifierSource: '潮の乙女' }
              : c
          ));
          addLog(`潮の乙女の効果: ${targetCard.name}のコストを1軽減！`, 'heal');
          return;
        }

        // 複数の場合は選択UI
        if (setPendingHandSelection) {
          setPendingHandSelection({
            message: 'コストを1軽減する水属性カードを選択してください',
            filter: (card: CardWithId) => card.attribute === '水',
            callback: (selectedCards: CardWithId[]) => {
              if (!selectedCards || selectedCards.length === 0) return;
              const selectedCard = selectedCards[0];
              setMyHand(prev => prev.map(c =>
                c.uniqueId === selectedCard.uniqueId
                  ? { ...c, tempCostModifier: (c.tempCostModifier || 0) - 1, tempCostModifierSource: '潮の乙女' }
                  : c
              ));
              addLog(`潮の乙女の効果: ${selectedCard.name}のコストを1軽減！`, 'heal');
            },
            selectionOwner: context.currentPlayer,
          } as any);
          return;
        }

        // フォールバック: 最初の水属性カードを選択
        const targetCard = waterCards[0];
        setMyHand(prev => prev.map(c =>
          c.uniqueId === targetCard.uniqueId
            ? { ...c, tempCostModifier: (c.tempCostModifier || 0) - 1, tempCostModifierSource: '潮の乙女' }
            : c
        ));
        addLog(`潮の乙女の効果: ${targetCard.name}のコストを1軽減！`, 'heal');
      },
    },
  ],

  /**
   * C0000043: 深海のクラーケン
   * 【墓地発動】このカードが墓地にあるときSPコスト4を払い、このカードを再度召喚可能。
   */
  C0000043: [
    {
      type: TRIGGER_TYPES.ON_MAIN_PHASE_FROM_GRAVEYARD,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '墓地発動: SP4払い自己蘇生',
      priority: TRIGGER_PRIORITIES.NORMAL,
      costCheck: (context: TriggerContext) => {
        const { myActiveSP } = getPlayerContext(context);
        return myActiveSP >= 4;
      },
      effect: (context: TriggerContext) => {
        const { card, addLog, summoningSickness = false } = context;
        const { myField, setMyField, myActiveSP, setMyActiveSP, setMyRestedSP, setMyGraveyard, currentPlayer } = getPlayerContext(context);

        if (myActiveSP < 4) {
          addLog('深海のクラーケンの効果: SPが足りません', 'info');
          return false;
        }

        // 空きスロットを探す
        const emptySlotIndex = myField.findIndex((slot) => slot === null);
        if (emptySlotIndex === -1) {
          addLog('深海のクラーケンの効果: フィールドに空きがありません', 'info');
          return false;
        }

        // SPコストを支払う
        setMyActiveSP((prev) => prev - 4);
        setMyRestedSP((prev) => prev + 4);

        // 墓地から除外
        setMyGraveyard((prev) => prev.filter((c) => (c as any).uniqueId !== (card as any)?.uniqueId));

        // フィールドに配置
        const resurrectedMonster = {
          ...card,
          currentHp: card?.hp ?? 0,
          currentAttack: card?.attack ?? 0,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          owner: currentPlayer,
          charges: [],
          statusEffects: [],
        } as unknown as MonsterInstance;

        setMyField((prev) => {
          const newField = [...prev];
          newField[emptySlotIndex] = resurrectedMonster;
          return newField;
        });

        addLog(`深海のクラーケンを墓地からSP4で蘇生！`, 'heal');
        return true;
      },
    },
  ],

  /**
   * C0000044: 水晶のマーメイド
   * 【召喚時】手札の水属性モンスター1体選び、次の自分のエンドフェイズまで召喚コストを1軽減。
   */
  C0000044: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 手札の水属性モンスター1体のコスト-1（エンドフェイズまで）',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingHandSelection } = context;
        const { myHand, setMyHand } = getPlayerContext(context);

        // 水属性モンスターをフィルタ
        const waterMonsters = myHand.filter(c => c.type === 'monster' && c.attribute === '水');

        if (waterMonsters.length === 0) {
          addLog('水晶のマーメイドの効果: 手札に水属性モンスターがありません', 'info');
          return;
        }

        // 1体のみの場合は自動選択
        if (waterMonsters.length === 1) {
          const targetCard = waterMonsters[0];
          setMyHand(prev => prev.map(c =>
            c.uniqueId === targetCard.uniqueId
              ? {
                  ...c,
                  tempCostModifier: (c.tempCostModifier || 0) - 1,
                  tempCostModifierSource: '水晶のマーメイド',
                  tempCostModifierUntilEndPhase: true, // エンドフェイズまでの一時軽減
                }
              : c
          ));
          addLog(`水晶のマーメイドの効果: ${targetCard.name}のコストを1軽減！`, 'heal');
          return;
        }

        // 複数の場合は選択UI
        if (setPendingHandSelection) {
          setPendingHandSelection({
            message: 'コストを1軽減する水属性モンスターを選択してください',
            filter: (card: CardWithId) => card.type === 'monster' && card.attribute === '水',
            callback: (selectedCards: CardWithId[]) => {
              if (!selectedCards || selectedCards.length === 0) return;
              const selectedCard = selectedCards[0];
              setMyHand(prev => prev.map(c =>
                c.uniqueId === selectedCard.uniqueId
                  ? {
                      ...c,
                      tempCostModifier: (c.tempCostModifier || 0) - 1,
                      tempCostModifierSource: '水晶のマーメイド',
                      tempCostModifierUntilEndPhase: true,
                    }
                  : c
              ));
              addLog(`水晶のマーメイドの効果: ${selectedCard.name}のコストを1軽減！`, 'heal');
            },
            selectionOwner: context.currentPlayer,
          } as any);
          return;
        }

        // フォールバック: 最初の水属性モンスターを選択
        const targetCard = waterMonsters[0];
        setMyHand(prev => prev.map(c =>
          c.uniqueId === targetCard.uniqueId
            ? {
                ...c,
                tempCostModifier: (c.tempCostModifier || 0) - 1,
                tempCostModifierSource: '水晶のマーメイド',
                tempCostModifierUntilEndPhase: true,
              }
            : c
        ));
        addLog(`水晶のマーメイドの効果: ${targetCard.name}のコストを1軽減！`, 'heal');
      },
    },
  ],

  /**
   * C0000045: 海流の守護者
   * 【墓地発動】このカードが墓地にある場合、自分のターン終了時にレスト状態のSPトークンを１つアクティブにする。
   */
  C0000045: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_FROM_GRAVEYARD,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '墓地・エンドフェイズ: レストSP1個をアクティブに',
      priority: TRIGGER_PRIORITIES.NORMAL,
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        if (myRestedSP > 0) {
          setMyRestedSP((prev) => prev - 1);
          setMyActiveSP((prev) => prev + 1);
          addLog('海流の守護者の墓地効果: レストSP1個をアクティブにした', 'info');
        } else {
          addLog('海流の守護者の墓地効果: レスト状態のSPがありません', 'info');
        }
        return true;
      },
    },
  ],

  /**
   * C0000053: 母なる大海
   * 【常時】水属性モンスターの攻撃力300アップ。（continuousEffects/fieldCards.jsで実装）
   * 【自分エンドフェイズ時】自分のレスト状態のSPトークンを1つアクティブにする。
   * 【エンドフェイズ時】「凍結」状態の相手モンスターがいる場合、相手プレイヤーに300ダメージを与える。
   */
  C0000053: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: レストSP1個をアクティブに',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        if (myRestedSP > 0) {
          setMyRestedSP((prev) => prev - 1);
          setMyActiveSP((prev) => prev + 1);
          addLog('母なる大海の効果: レストSP1個をアクティブにした', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 凍結モンスターいれば相手に300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField } = getPlayerContext(context);

        // 凍結状態のモンスターがいるかチェック
        const frozenMonsters = opponentField.filter(monster =>
          monster &&
          monster.statusEffects &&
          monster.statusEffects.some(effect => effect.type === STATUS_EFFECT_TYPES.FREEZE)
        );

        if (frozenMonsters.length > 0) {
          addLog(`母なる大海の効果: 凍結モンスター${frozenMonsters.length}体、相手に300ダメージ！`, 'damage');
          conditionalDamage(context as any, 300, 'opponent');
        }
      },
    },
  ],

  /**
   * C0000054: 珊瑚の迷宮
   * 【相手モンスターの攻撃時】、その攻撃力を200ダウン。
   * 【自分エンドフェイズ時】自分のライフを300回復。
   */
  C0000054: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 自分のライフ300回復',
      effect: (context: TriggerContext) => {
        healLife(context as any, 300, true);
      },
    },
  ],

  /**
   * C0000055: 深淵の潮流
   * 【常時】水属性モンスターの召喚コストを1軽減。
   * 【エンドフェイズ時】場に水属性モンスターが3体以上いる場合、相手プレイヤーに500ダメージを与える。
   */
  C0000055: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 水属性モンスターのコスト-1',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('深淵の潮流の常時効果: 水属性モンスターのコスト-1（未実装）', 'info');
        // TODO: フィールドカードの常時効果システムが必要
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 水属性3体以上で相手に500',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        const waterCount = myField.filter((monster) =>
          monster && monster.attribute === '水'
        ).length;

        if (waterCount >= 3) {
          addLog(`深淵の潮流の効果: 水属性${waterCount}体、相手に500ダメージ`, 'damage');
          conditionalDamage(context as any, 500, 'opponent');
        }
      },
    },
  ],

  /**
   * C0000142: ブリザードマスター
   * 【召喚時】デッキから《ブリザード》または《猫》魔法カード1枚を手札に加える。
   */
  C0000142: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 《ブリザード》/《猫》魔法をサーチ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        // 対象カードをフィルタ
        const targetFilter = (card: CardWithId) =>
          card.type === 'magic' &&
          (card.name?.includes('ブリザード') || card.name?.includes('猫'));

        const matchingCards = myDeck.filter(targetFilter);

        if (matchingCards.length === 0) {
          addLog('ブリザードマスターの効果: 対象カードが見つかりませんでした', 'info');
          return;
        }

        // 1枚のみの場合は自動選択
        if (matchingCards.length === 1) {
          const targetCard = matchingCards[0];
          setMyDeck(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
          setMyHand(prev => [...prev, targetCard]);
          addLog(`ブリザードマスターの効果: ${targetCard.name}を手札に加えた！`, 'heal');
          return;
        }

        // 複数ある場合はデッキ選択UIを表示
        if (setPendingDeckReview) {
          setPendingDeckReview({
            cards: matchingCards,
            title: 'ブリザードマスターの効果',
            message: '手札に加える《ブリザード》または《猫》魔法カードを1枚選択してください',
            allowReorder: false,
            selectMode: {
              enabled: true,
              count: 1,
            },
            onSelect: (selectedCards: CardWithId[], remainingCards: CardWithId[]) => {
              // DeckReviewModalは常に配列を渡すため、配列の最初の要素を取得
              const selectedCard = selectedCards[0];
              if (selectedCard) {
                setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
                setMyHand(prev => [...prev, selectedCard]);
                addLog(`ブリザードマスターの効果: ${selectedCard.name}を手札に加えた！`, 'heal');
              }
            },
            onCancel: () => {
              addLog('ブリザードマスターの効果: キャンセルしました', 'info');
            },
            selectionOwner: context.currentPlayer,
          } as any);
          return;
        }

        // フォールバック: 最初のカードを選択
        const targetCard = matchingCards[0];
        setMyDeck(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
        setMyHand(prev => [...prev, targetCard]);
        addLog(`ブリザードマスターの効果: ${targetCard.name}を手札に加えた！`, 'heal');
      },
    },
  ],

  /**
   * C0000143: 氷猫の使い魔
   * 【墓地発動】このカードが墓地にある場合、自分のターン終了時に墓地の「ブリザードキャット」モンスター1体を手札に戻す（1度だけ）。
   */
  C0000143: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_FROM_GRAVEYARD,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '墓地・エンドフェイズ: ブリザードキャットを手札に（1度だけ）',
      priority: TRIGGER_PRIORITIES.NORMAL,
      usedOnce: false, // 1度だけ発動のフラグ
      effect: (context: TriggerContext) => {
        const { card, addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyHand } = getPlayerContext(context);

        // 1度だけの制限チェック（カードにフラグを設定）
        if (card && (card as any).graveyardEffectUsed) {
          return false; // 既に使用済み
        }

        // 墓地から「ブリザードキャット」を探す（自分自身以外）
        const targetCard = myGraveyard.find((c) =>
          (c as any).uniqueId !== (card as any)?.uniqueId &&
          c.type === 'monster' &&
          c.name &&
          c.name.includes('ブリザードキャット')
        );

        if (!targetCard) {
          addLog('氷猫の使い魔の墓地効果: 墓地に「ブリザードキャット」がいません', 'info');
          return false;
        }

        // 墓地から手札に戻す
        setMyGraveyard((prev) => {
          // 自分自身にフラグを設定
          return prev.map((c) => {
            if ((c as any).uniqueId === (card as any)?.uniqueId) {
              return { ...c, graveyardEffectUsed: true };
            }
            return c;
          }).filter((c) => (c as any).uniqueId !== (targetCard as any).uniqueId);
        });
        setMyHand((prev) => [...prev, targetCard]);

        addLog(`氷猫の使い魔の墓地効果: ${targetCard.name}を手札に戻した`, 'info');
        return true;
      },
    },
  ],

  /**
   * C0000144: ブリザードキャット・フロスト
   * 【召喚時】相手モンスター1体を「凍結（攻撃力半減＋行動不能）」にする、次のターン開始時に50%で解除。
   */
  C0000144: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体を「凍結」',
      effect: (context: TriggerContext) => {
        // 凍結: 攻撃力半減＋行動不能、次のターン開始時に50%で解除
        selectAndApplyStatusToOpponent(context as any,
          STATUS_EFFECT_TYPES.FREEZE,
          {
            duration: -1, // 永続（解除判定で消える）
            removeChance: 0.5, // 50%で解除
          },
          'ブリザードキャット・フロスト'
        );
      },
    },
  ],

  /**
   * C0000145: ブリザードキャット・スノウ
   * 【召喚時】相手モンスター1体を「凍結（攻撃力半減＋行動不能）」にする、次のターン開始時に50%で解除。
   */
  C0000145: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体を「凍結」',
      effect: (context: TriggerContext) => {
        // 凍結: 攻撃力半減＋行動不能、次のターン開始時に50%で解除
        selectAndApplyStatusToOpponent(context as any,
          STATUS_EFFECT_TYPES.FREEZE,
          {
            duration: -1, // 永続（解除判定で消える）
            removeChance: 0.5, // 50%で解除
          },
          'ブリザードキャット・スノウ'
        );
      },
    },
  ],

  /**
   * C0000146: 氷結の守護猫
   * 【自破壊時】デッキから《ブリザード》フィールドカード1枚を手札に加える。
   */
  C0000146: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 《ブリザード》フィールドをサーチ',
      effect: (context: TriggerContext) => {
        const found = searchCard(context as any, (card) => {
          return card.type === 'field' && card.name.includes('ブリザード');
        });

        if (!found) {
          context.addLog('氷結の守護猫の効果: 《ブリザード》フィールドカードが見つかりませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000147: ブリザードキャット・シャード
   * 【召喚時】相手モンスター1体に、場にいる《ブリザードキャット》×500ダメージを与える。
   */
  C0000147: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 《ブリザードキャット》×500ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        // 場の《ブリザードキャット》をカウント
        const blizzardCatCount = myField.filter((monster) =>
          monster && monster.name && monster.name.includes('ブリザードキャット')
        ).length;

        if (blizzardCatCount === 0) {
          addLog('ブリザードキャット・シャードの効果: 《ブリザードキャット》がいません', 'info');
          return;
        }

        const damage = blizzardCatCount * 500;

        // 相手モンスターを取得
        const validTargets = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster }) => monster !== null);

        if (validTargets.length === 0) {
          addLog('ブリザードキャット・シャードの効果: 相手モンスターがいません', 'info');
          return;
        }

        const applyDamage = (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          const newHp = Math.max(0, target.currentHp - damage);
          addLog(`${target.name}に${damage}ダメージ！（残りHP: ${newHp}）`, 'damage');

          if (newHp <= 0) {
            addLog(`${target.name}が破壊された！`, 'damage');
            setOpponentGraveyard(prev => [...prev, target]);
            setOpponentField(prev => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
          } else {
            setOpponentField(prev => {
              const newField = [...prev];
              newField[targetIndex] = { ...target, currentHp: newHp };
              return newField;
            });
          }
        };

        // 1体のみの場合は自動選択
        if (validTargets.length === 1) {
          addLog(`ブリザードキャット×${blizzardCatCount}で${damage}ダメージ！`, 'info');
          applyDamage(validTargets[0].index);
          return;
        }

        // 複数いる場合は選択UI
        if (setPendingMonsterTarget) {
          addLog(`ブリザードキャット×${blizzardCatCount}で${damage}ダメージ！対象を選択...`, 'info');
          setPendingMonsterTarget({
            message: `${damage}ダメージを与える相手モンスターを選択してください`,
            targetPlayer: 'opponent',
            callback: (selectedIndex: number) => {
              applyDamage(selectedIndex);
            },
            selectionOwner: context.currentPlayer,
          } as any);
          return;
        }

        // フォールバック
        addLog(`ブリザードキャット×${blizzardCatCount}で${damage}ダメージ！`, 'info');
        applyDamage(validTargets[0].index);
      },
    },
  ],

  /**
   * C0000149: ブリザードマスターの愛猫・ミスティ
   * 【常時】場に《ブリザードマスター》がいる場合、攻撃力が1000アップ。
   */
  C0000149: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 《ブリザードマスター》いる時攻撃力+1000',
      effect: (context: TriggerContext) => {
        const { monsterIndex } = context;
        const { myField } = getPlayerContext(context);

        const hasBlizzardMaster = myField.some((monster, idx) =>
          monster &&
          idx !== monsterIndex &&
          monster.name &&
          monster.name.includes('ブリザードマスター')
        );

        if (hasBlizzardMaster) {
          modifyAttack(context as any, 1000, monsterIndex, false, true);
        }
      },
    },
  ],

  /**
   * C0000155: 氷猫の聖域
   * 【常時】《ブリザードキャット》モンスターの攻撃力を400アップ。
   * 【エンドフェイズ時】相手の場にいるモンスター1体の攻撃力を300ダウン。
   */
  C0000155: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 《ブリザードキャット》の攻撃力+400',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('氷猫の聖域の常時効果: 《ブリザードキャット》の攻撃力+400（未実装）', 'info');
        // TODO: フィールドカードの常時効果システムが必要
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 相手モンスター1体の攻撃力-300',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField } = getPlayerContext(context);
        const opponentMonsters = opponentField.filter((m) => m !== null);
        if (opponentMonsters.length === 0) {
          addLog('氷猫の聖域の効果: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        // 最初のモンスターをターゲット（UIで選択させるべきだが、簡略化）
        const targetIndex = opponentField.findIndex((m) => m !== null);
        if (targetIndex !== -1) {
          modifyAttack(context as any, -300, targetIndex, true, false);
          addLog('氷猫の聖域の効果: 相手モンスターの攻撃力-300', 'info');
        }
      },
    },
  ],

  /**
   * C0000156: 凍結の猫森
   * 【エンドフェイズ時】場に水属性モンスターがいるとき、相手プレイヤーに200ダメージ。
   * 【自分エンドフェイズ時】場に《ブリザードキャット》が2体以上いるとき、レスト状態のSPトークンを2個アクティブにする。
   */
  C0000156: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 水属性いれば相手に200、《ブリザードキャット》2体以上でSP回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        // 水属性モンスターチェック
        const hasWater = myField.some((monster) => monster && monster.attribute === '水');
        if (hasWater) {
          addLog('凍結の猫森の効果: 相手に200ダメージ', 'damage');
          conditionalDamage(context as any, 200, 'opponent');
        }

        // 《ブリザードキャット》2体以上チェック
        const blizzardCatCount = myField.filter((monster) =>
          monster && monster.name && monster.name.includes('ブリザードキャット')
        ).length;

        if (blizzardCatCount >= 2) {
          const toActivate = Math.min(myRestedSP, 2);
          if (toActivate > 0) {
            setMyRestedSP((prev) => prev - toActivate);
            setMyActiveSP((prev) => prev + toActivate);
            addLog(`凍結の猫森の効果: レストSP${toActivate}個をアクティブにした`, 'info');
          }
        }
      },
    },
  ],

  /**
   * C0000157: ブリザードキャット・エターナル
   * 【召喚時】相手の場のモンスター全てを凍結（攻撃力半減＋行動不能、ターン開始時に50%解除）にする。
   * 【自壊時】自分のライフを3000減らす。
   */
  C0000157: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター全てを「凍結」',
      effect: (context: TriggerContext) => {
        // 凍結: 攻撃力半減＋行動不能、ターン開始時50%解除
        const count = applyStatusToAllOpponentMonsters(context as any,
          STATUS_EFFECT_TYPES.FREEZE,
          {
            duration: -1, // 永続（解除判定で消える）
            removeChance: 0.5, // 50%で解除
          },
          'ブリザードキャット・エターナル'
        );
        if (count === 0) {
          context.addLog('相手の場にモンスターがいません', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 自分のライフ-3000',
      effect: (context: TriggerContext) => {
        context.addLog('ブリザードキャット・エターナルの破壊時効果: 自分のライフ-3000', 'damage');
        conditionalDamage(context as any, 3000, 'self');
      },
    },
  ],

  /**
   * C0000199: 永遠の氷結宮殿
   * 【発動時】相手モンスター全体を「凍結」（攻撃半減＋行動不能、ターン開始時50％解除）状態にする。
   * 【自分エンドフェイズ時】自分の場に水属性3体以上いる場合、場のモンスター1体を「凍結」状態にする。
   * 【相手エンドフェイズ時】凍結モンスター1体につき相手プレイヤーに300ダメージを与える。
   * 【常時】自分のSP上限が4になる。
   * 【自壊時】自分のライフを2000減らす。
   */
  C0000199: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '発動時: 相手モンスター全体を「凍結」',
      effect: (context: TriggerContext) => {
        // 凍結: 攻撃半減＋行動不能、ターン開始時50%解除
        const count = applyStatusToAllOpponentMonsters(context as any,
          STATUS_EFFECT_TYPES.FREEZE,
          {
            duration: -1,
            removeChance: 0.5,
          },
          '永遠の氷結宮殿'
        );
        if (count === 0) {
          context.addLog('相手の場にモンスターがいません', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 自分のライフ-2000',
      effect: (context: TriggerContext) => {
        context.addLog('永遠の氷結宮殿の破壊時効果: 自分のライフ-2000', 'damage');
        conditionalDamage(context as any, 2000, 'self');
      },
    },
  ],

  /**
   * C0000233: 水漏れのリリカ
   * 【召喚時】相手の場にいるモンスター1体を「濡れ」状態にする（次のターン終了まで受けるダメージ倍）。
   */
  C0000233: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 相手モンスター1体を「濡れ」状態に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('水漏れのリリカの効果: 相手モンスター1体を「濡れ」状態に（未実装）', 'info');
        // TODO: 状態異常システムの実装が必要
      },
    },
  ],

  /**
   * C0000283: 酸毒竜
   * 【自分エンドフェイズ時】相手に300ダメージを与える。
   */
  C0000283: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 相手に300ダメージ',
      effect: (context: TriggerContext) => {
        context.addLog('酸毒竜の効果: 相手に300ダメージ', 'damage');
        conditionalDamage(context as any, 300, 'opponent');
      },
    },
  ],

  /**
   * C0000313: アクア・リヴァイアサン
   * 【召喚時】相手の場にいるモンスター全ての攻撃力をターン終了時まで500下げる。
   */
  C0000313: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター全ての攻撃力-500',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setOpponentField } = getPlayerContext(context);

        let affected = 0;
        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              affected++;
              return {
                ...monster,
                currentAttack: Math.max(0, (monster.attack ?? 0) - 500),
                attackModified: true,
              };
            }
            return monster;
          });
        });

        if (affected > 0) {
          addLog(`アクア・リヴァイアサンの効果: 相手モンスター${affected}体の攻撃力-500`, 'info');
        }
      },
    },
  ],

  /**
   * C0000330: アクアレギナの漂流漁師
   * 【召喚時】墓地の『アクアレギア』と名の付くモンスター1体をデッキに戻すと、レスト状態のSPトークンをアクティブにする。
   */
  C0000330: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 墓地の『アクアレギア』をデッキに戻しSP回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyDeck, myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        // 墓地から『アクアレギア』を探す
        const targetCard = myGraveyard.find((card) =>
          card.type === 'monster' && card.name && card.name.includes('アクアレギア')
        );

        if (!targetCard) {
          addLog('アクアレギナの漂流漁師の効果: 墓地に『アクアレギア』モンスターがいません', 'info');
          return;
        }

        // 墓地からデッキに戻す
        setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));
        setMyDeck((prev) => [...prev, targetCard]);

        // レストSPをアクティブに
        if (myRestedSP > 0) {
          setMyRestedSP((prev) => prev - 1);
          setMyActiveSP((prev) => prev + 1);
          addLog(`${targetCard.name}をデッキに戻し、レストSP1個をアクティブにした`, 'info');
        }
      },
    },
  ],

  /**
   * C0000332: アクアレギナの守護者
   * 【召喚時】自分の墓地の『アクアレギア』モンスター1体の攻撃力の半分をこのカードのHPに加える（最大1000）。
   */
  C0000332: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地の『アクアレギア』の攻撃力半分をHPに加算',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog } = context;
        const { myGraveyard, myField, setMyField } = getPlayerContext(context);

        // 墓地から『アクアレギア』を探す
        const targetCard = myGraveyard.find((card) =>
          card.type === 'monster' && card.name && card.name.includes('アクアレギア')
        );

        if (!targetCard) {
          addLog('アクアレギナの守護者の効果: 墓地に『アクアレギア』モンスターがいません', 'info');
          return;
        }

        const hpBonus = Math.min(Math.floor((targetCard.attack ?? 0) / 2), 1000);

        setMyField((prev) => {
          return prev.map((monster, idx) => {
            if (idx === monsterIndex && monster) {
              addLog(`アクアレギナの守護者の効果: HPを${hpBonus}増加`, 'info');
              return {
                ...monster,
                maxHp: ((monster as any).maxHp ?? monster.hp ?? 0) + hpBonus,
                currentHp: monster.currentHp + hpBonus,
              } as MonsterInstance;
            }
            return monster;
          });
        });
      },
    },
  ],

  /**
   * C0000333: ヴェルゼファールの信徒・深みの儀式者
   * 【魂結】: 召喚時、場にいる別の『ヴェルゼファール』モンスター1体とリンクし、双方の攻撃力を100アップ。
   */
  C0000333: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '【魂結】: 『ヴェルゼファール』とリンクし双方ATK+100',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField } = getPlayerContext(context);

        const effect = getKonketsuEffect('C0000333');
        if (!effect) return;

        const sourceMonster = myField[monsterIndex ?? -1];
        if (!sourceMonster) return;

        const targets = findLinkableTargets(myField, effect.targetCondition, sourceMonster.uniqueId);

        if (targets.length === 0) {
          addLog('【魂結】リンク対象の『ヴェルゼファール』モンスターがいません', 'info');
          return;
        }

        if (targets.length === 1) {
          executeKonketsuLink(
            sourceMonster, monsterIndex!,
            targets[0].monster, targets[0].slotIndex,
            effect, setMyField as any, addLog as any
          );
          return;
        }

        // 複数対象がいる場合は選択UI
        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '【魂結】リンク対象を選択してください',
            targetPlayer: 'self',
            validIndices: targets.map(t => t.slotIndex),
            callback: (selectedIndex: number) => {
              const target = targets.find(t => t.slotIndex === selectedIndex);
              if (target) {
                executeKonketsuLink(
                  sourceMonster, monsterIndex!,
                  target.monster, target.slotIndex,
                  effect, setMyField as any, addLog as any
                );
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
  ],

  /**
   * C0000334: ヴェルゼファールの眷属・クラディオム
   * 【魂結】: 召喚時、場にいる別の《ヴェルゼファール》モンスター1体とリンクし、双方の攻撃力を300アップ。
   * 【深蝕】: このカードが場にいる間、自分のターン終了時、相手モンスター1体の攻撃力を500下げ、0になると破壊。
   */
  C0000334: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '【魂結】: 『ヴェルゼファール』とリンクし双方ATK+300',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField } = getPlayerContext(context);

        const effect = getKonketsuEffect('C0000334');
        if (!effect) return;

        const sourceMonster = myField[monsterIndex ?? -1];
        if (!sourceMonster) return;

        const targets = findLinkableTargets(myField, effect.targetCondition, sourceMonster.uniqueId);

        if (targets.length === 0) {
          addLog('【魂結】リンク対象の『ヴェルゼファール』モンスターがいません', 'info');
          return;
        }

        if (targets.length === 1) {
          executeKonketsuLink(
            sourceMonster, monsterIndex!,
            targets[0].monster, targets[0].slotIndex,
            effect, setMyField as any, addLog as any
          );
          return;
        }

        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '【魂結】リンク対象を選択してください',
            targetPlayer: 'self',
            validIndices: targets.map(t => t.slotIndex),
            callback: (selectedIndex: number) => {
              const target = targets.find(t => t.slotIndex === selectedIndex);
              if (target) {
                executeKonketsuLink(
                  sourceMonster, monsterIndex!,
                  target.monster, target.slotIndex,
                  effect, setMyField as any, addLog as any
                );
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '【深蝕】: 相手モンスター1体のATK-500、0で破壊',
      effect: (context: TriggerContext) => {
        const { card, setPendingMonsterTarget } = context;
        executeShinsyokuEffect(context as any, card?.name || 'クラディオム', setPendingMonsterTarget);
      },
    },
  ],

  /**
   * C0000335: ヴェルゼファールの眷属・シスラゴン
   * 【魂結】: 召喚時、場にいる別の《ヴェルゼファール》モンスター1体とリンクし、双方に『ターン終了時、相手プレイヤーに300ダメージ』を付与。
   * 【深蝕】: このカードが場にいる間、自分のターン終了時、相手モンスター1体の攻撃力を500下げ、0になると破壊。
   */
  C0000335: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '【魂結】: 『ヴェルゼファール』とリンクし双方にターン終了時300ダメージ付与',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField } = getPlayerContext(context);

        const effect = getKonketsuEffect('C0000335');
        if (!effect) return;

        const sourceMonster = myField[monsterIndex ?? -1];
        if (!sourceMonster) return;

        const targets = findLinkableTargets(myField, effect.targetCondition, sourceMonster.uniqueId);

        if (targets.length === 0) {
          addLog('【魂結】リンク対象の『ヴェルゼファール』モンスターがいません', 'info');
          return;
        }

        if (targets.length === 1) {
          executeKonketsuLink(
            sourceMonster, monsterIndex!,
            targets[0].monster, targets[0].slotIndex,
            effect, setMyField as any, addLog as any
          );
          return;
        }

        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '【魂結】リンク対象を選択してください',
            targetPlayer: 'self',
            validIndices: targets.map(t => t.slotIndex),
            callback: (selectedIndex: number) => {
              const target = targets.find(t => t.slotIndex === selectedIndex);
              if (target) {
                executeKonketsuLink(
                  sourceMonster, monsterIndex!,
                  target.monster, target.slotIndex,
                  effect, setMyField as any, addLog as any
                );
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '【深蝕】: 相手モンスター1体のATK-500、0で破壊',
      effect: (context: TriggerContext) => {
        const { card, setPendingMonsterTarget } = context;
        executeShinsyokuEffect(context as any, card?.name || 'シスラゴン', setPendingMonsterTarget);
      },
    },
  ],

  /**
   * C0000336: ヴェルゼファールの眷属・ルミナクール
   * 【魂結】: 召喚時、場にいる別の『ヴェルゼファール』モンスター1体とリンクし、双方のHPを800アップ。
   * 【深蝕】: このカードが場にいる間、自分のターン終了時、相手モンスター1体の攻撃力を500下げ、0になると破壊。
   */
  C0000336: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '【魂結】: 『ヴェルゼファール』とリンクし双方HP+800',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField } = getPlayerContext(context);

        const effect = getKonketsuEffect('C0000336');
        if (!effect) return;

        const sourceMonster = myField[monsterIndex ?? -1];
        if (!sourceMonster) return;

        const targets = findLinkableTargets(myField, effect.targetCondition, sourceMonster.uniqueId);

        if (targets.length === 0) {
          addLog('【魂結】リンク対象の『ヴェルゼファール』モンスターがいません', 'info');
          return;
        }

        if (targets.length === 1) {
          executeKonketsuLink(
            sourceMonster, monsterIndex!,
            targets[0].monster, targets[0].slotIndex,
            effect, setMyField as any, addLog as any
          );
          return;
        }

        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '【魂結】リンク対象を選択してください',
            targetPlayer: 'self',
            validIndices: targets.map(t => t.slotIndex),
            callback: (selectedIndex: number) => {
              const target = targets.find(t => t.slotIndex === selectedIndex);
              if (target) {
                executeKonketsuLink(
                  sourceMonster, monsterIndex!,
                  target.monster, target.slotIndex,
                  effect, setMyField as any, addLog as any
                );
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '【深蝕】: 相手モンスター1体のATK-500、0で破壊',
      effect: (context: TriggerContext) => {
        const { card, setPendingMonsterTarget } = context;
        executeShinsyokuEffect(context as any, card?.name || 'ルミナクール', setPendingMonsterTarget);
      },
    },
  ],

  /**
   * C0000337: ヴェルゼファールの眷属・タラッサロス
   * 【魂結】: 召喚時、場にいる別の『ヴェルゼファール』モンスター1体とリンクし、双方の攻撃力を1000アップ。
   *          さらに双方に『自分ターン終了時、相手プレイヤーに800ダメージ』を付与。
   * 【深蝕】: このカードが場にいる間、自分のターン終了時、相手モンスター1体の攻撃力を500下げ、0になると破壊。
   */
  C0000337: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '【魂結】: 『ヴェルゼファール』とリンクし双方ATK+1000、ターン終了時800ダメージ付与',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField } = getPlayerContext(context);

        const effect = getKonketsuEffect('C0000337');
        if (!effect) return;

        const sourceMonster = myField[monsterIndex ?? -1];
        if (!sourceMonster) return;

        const targets = findLinkableTargets(myField, effect.targetCondition, sourceMonster.uniqueId);

        if (targets.length === 0) {
          addLog('【魂結】リンク対象の『ヴェルゼファール』モンスターがいません', 'info');
          return;
        }

        if (targets.length === 1) {
          executeKonketsuLink(
            sourceMonster, monsterIndex!,
            targets[0].monster, targets[0].slotIndex,
            effect, setMyField as any, addLog as any
          );
          return;
        }

        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '【魂結】リンク対象を選択してください',
            targetPlayer: 'self',
            validIndices: targets.map(t => t.slotIndex),
            callback: (selectedIndex: number) => {
              const target = targets.find(t => t.slotIndex === selectedIndex);
              if (target) {
                executeKonketsuLink(
                  sourceMonster, monsterIndex!,
                  target.monster, target.slotIndex,
                  effect, setMyField as any, addLog as any
                );
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '【深蝕】: 相手モンスター1体のATK-500、0で破壊',
      effect: (context: TriggerContext) => {
        const { card, setPendingMonsterTarget } = context;
        executeShinsyokuEffect(context as any, card?.name || 'タラッサロス', setPendingMonsterTarget);
      },
    },
  ],

  /**
   * C0000338: アクアレギアの廃墟
   * 【常時】『アクアレギナ』または『ヴェルゼファール』モンスターのHPを600アップ。
   * 【自分エンドフェイズ時】自分の墓地の『アクアレギナ』モンスター1体を手札に戻すことができる。
   */
  C0000338: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 『アクアレギナ』/『ヴェルゼファール』のHP+600',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('アクアレギアの廃墟の常時効果: HP+600（continuousEffectsで処理）', 'info');
        // continuousEffects/effectDefinitions/fieldCards.jsで実装済み
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: 'エンドフェイズ: 墓地の『アクアレギナ』を手札に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyHand } = getPlayerContext(context);

        // 墓地から『アクアレギナ』を探す
        const targetCard = myGraveyard.find((card) =>
          card.type === 'monster' && card.name && card.name.includes('アクアレギナ')
        );

        if (!targetCard) {
          addLog('アクアレギアの廃墟の効果: 墓地に『アクアレギナ』モンスターがいません', 'info');
          return;
        }

        // 墓地から手札に戻す
        setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));
        setMyHand((prev) => [...prev, targetCard]);
        addLog(`${targetCard.name}を手札に戻した`, 'info');
      },
    },
  ],

  /**
   * C0000339: アクアレギナの動力-エテルノス・コア (Phase Card)
   * 初期効果:【常時】 『アクアレギナ』または『ヴェルゼファール』モンスターの攻撃力を400アップ。（continuousEffects/phaseCards.jsで実装済み）
   * 1枚重ね: 【自分エンドフェイズ時】場に『アクアレギナ』または『ヴェルゼファール』モンスターがいる場合、相手プレイヤーに500ダメージ。
   * 2枚重ね: 【自分の『アクアレギナ』または『ヴェルゼファール』モンスターの召喚時】そのモンスターの攻撃力を500アップ。
   * 3枚重ね: 相手フィールド全体に2000ダメージを与え、自分のライフを1000減らす。
   */
  C0000339: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 『アクアレギナ』/『ヴェルゼファール』の攻撃力+400',
      effect: (context: TriggerContext) => {
        // 常時効果はcontinuousEffects/phaseCards.jsで実装済み
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚重ね: 該当モンスターがいれば500ダメージ',
      condition: (context: TriggerContext) => {
        const { myPhaseCard } = getPlayerContext(context);
        return myPhaseCard && myPhaseCard.id === 'C0000339' && (myPhaseCard.stage || 0) >= 1;
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        // 場に『アクアレギナ』または『ヴェルゼファール』モンスターがいるか確認
        const hasValidMonster = myField.some(m =>
          m && m.name && (m.name.includes('アクアレギナ') || m.name.includes('ヴェルゼファール'))
        );

        if (hasValidMonster) {
          conditionalDamage(context as any, 500, 'opponent');
          addLog('アクアレギナの動力【1枚重ね】: 相手プレイヤーに500ダメージ！', 'damage');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '2枚重ね: 召喚時ATK+500',
      condition: (context: TriggerContext) => {
        const { card } = context;
        const { myPhaseCard } = getPlayerContext(context);
        const isValidMonster = card && card.name &&
          (card.name.includes('アクアレギナ') || card.name.includes('ヴェルゼファール'));
        return isValidMonster &&
               myPhaseCard && myPhaseCard.id === 'C0000339' && (myPhaseCard.stage || 0) >= 2;
      },
      effect: (context: TriggerContext) => {
        const { card, addLog, slotIndex } = context;
        const { setMyField } = getPlayerContext(context);

        if (slotIndex !== undefined && slotIndex !== null) {
          setMyField(prev => {
            const newField = [...prev];
            const monster = newField[slotIndex];
            if (monster) {
              newField[slotIndex] = {
                ...monster,
                currentAttack: (monster.currentAttack || (monster.attack ?? 0)) + 500,
              };
            }
            return newField;
          });
          addLog(`アクアレギナの動力【2枚重ね】: ${card?.name}の攻撃力+500！`, 'info');
        }
      },
    },
  ],

  /**
   * C0000340: 深海の支配者・ヴェルゼファール
   * 【召喚時】手札から『ヴェルゼファール』モンスター1体を場に召喚（コスト不要）。このターン、ダイレクトアタック不可。
   * 【魂結】: 召喚時、場にいる別の『ヴェルゼファール』モンスター1体とリンクし、双方に『自分ターン終了時、相手プレイヤーに800ダメージ』を付与。
   * 【深蝕】: このカードが場にいる間、自分のターン終了時、相手モンスター1体の攻撃力を500下げ、0になると破壊。
   * 【場を離れる時】自分のライフを1000減らす。
   */
  C0000340: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 『ヴェルゼファール』1体をコスト不要で召喚',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingHandSelection, setPendingMonsterTarget, summoningSickness = false } = context;
        const { myHand, setMyHand, myField, setMyField, currentPlayer } = getPlayerContext(context);

        // 手札から『ヴェルゼファール』モンスターを探す
        const verzefaalMonsters = myHand.filter(card =>
          card.type === 'monster' && card.name && card.name.includes('ヴェルゼファール')
        );

        if (verzefaalMonsters.length === 0) {
          addLog('深海の支配者・ヴェルゼファール: 手札に『ヴェルゼファール』モンスターがいません', 'info');
          return;
        }

        // 空きスロットを探す
        const emptySlotIndex = myField.findIndex(slot => slot === null);
        if (emptySlotIndex === -1) {
          addLog('深海の支配者・ヴェルゼファール: 場に空きがありません', 'info');
          return;
        }

        // 特殊召喚処理
        const performSpecialSummon = (selectedCard: CardWithId) => {
          // 手札から除去
          setMyHand(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

          // フィールドに配置
          setMyField(prev => {
            const newField = [...prev];
            const slotIndex = newField.findIndex(slot => slot === null);
            if (slotIndex !== -1) {
              newField[slotIndex] = {
                ...selectedCard,
                currentHp: selectedCard.hp ?? 0,
                currentAttack: selectedCard.attack ?? 0,
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                charges: [],
                statusEffects: [],
                owner: currentPlayer,
                canDirectAttack: false, // ダイレクトアタック不可フラグ
                usedSkillThisTurn: false,
                linkedTo: null,
                linkedBonus: { atk: 0, hp: 0 },
                linkedEndPhaseDamage: [],
              } as unknown as MonsterInstance;
            }
            return newField;
          });

          addLog(`深海の支配者・ヴェルゼファール: ${selectedCard.name}を特殊召喚！（このターン、ダイレクトアタック不可）`, 'heal');
        };

        // 1体のみの場合は自動選択
        if (verzefaalMonsters.length === 1) {
          performSpecialSummon(verzefaalMonsters[0]);
          return;
        }

        // 複数いる場合は選択UI
        if (setPendingHandSelection) {
          setPendingHandSelection({
            message: '特殊召喚する『ヴェルゼファール』モンスターを選択',
            filter: (card: CardWithId) => Boolean(card.type === 'monster' && card.name && card.name.includes('ヴェルゼファール')),
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards && selectedCards.length > 0) {
                performSpecialSummon(selectedCards[0]);
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          // フォールバック
          performSpecialSummon(verzefaalMonsters[0]);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      priority: TRIGGER_PRIORITIES.LOW, // 特殊召喚の後に発動
      description: '【魂結】: 『ヴェルゼファール』とリンクし双方にターン終了時800ダメージ付与',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField } = getPlayerContext(context);

        const effect = getKonketsuEffect('C0000340');
        if (!effect) return;

        const sourceMonster = myField[monsterIndex ?? -1];
        if (!sourceMonster) return;

        const targets = findLinkableTargets(myField, effect.targetCondition, sourceMonster.uniqueId);

        if (targets.length === 0) {
          addLog('【魂結】リンク対象の『ヴェルゼファール』モンスターがいません', 'info');
          return;
        }

        if (targets.length === 1) {
          executeKonketsuLink(
            sourceMonster, monsterIndex!,
            targets[0].monster, targets[0].slotIndex,
            effect, setMyField as any, addLog as any
          );
          return;
        }

        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '【魂結】リンク対象を選択してください',
            targetPlayer: 'self',
            validIndices: targets.map(t => t.slotIndex),
            callback: (selectedIndex: number) => {
              const target = targets.find(t => t.slotIndex === selectedIndex);
              if (target) {
                executeKonketsuLink(
                  sourceMonster, monsterIndex!,
                  target.monster, target.slotIndex,
                  effect, setMyField as any, addLog as any
                );
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '【深蝕】: 相手モンスター1体のATK-500、0で破壊',
      effect: (context: TriggerContext) => {
        const { card, setPendingMonsterTarget } = context;
        executeShinsyokuEffect(context as any, card?.name || 'ヴェルゼファール', setPendingMonsterTarget);
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '場を離れる時: 自分のライフ-1000',
      effect: (context: TriggerContext) => {
        context.addLog('深海の支配者・ヴェルゼファールの効果: 自分のライフ-1000', 'damage');
        conditionalDamage(context as any, 1000, 'self');
      },
    },
  ],

  /**
   * C0000363: 虹羽密林のアクアフェザー
   * 【自分エンドフェイズ時】自分のライフを800回復。
   */
  C0000363: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 自分のライフ800回復',
      effect: (context: TriggerContext) => {
        healLife(context as any, 800, true);
      },
    },
  ],

  /**
   * C0000375: 虹羽密林の霧花蛙・ヴェルミナ
   * 【召喚時】自分の手札1枚をデッキに戻しシャッフルする、その後、自分のSPトークンを1個レスト状態からアクティブに変える。
   */
  C0000375: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 手札1枚をデッキに戻しSP回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myHand, setMyHand, setMyDeck, myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        if (myHand.length === 0) {
          addLog('虹羽密林の霧花蛙・ヴェルミナの効果: 手札がありません', 'info');
          return;
        }

        // 最初のカードをデッキに戻す（UIで選択させるべきだが、簡略化）
        const returnedCard = myHand[0];
        setMyHand((prev) => prev.slice(1));
        setMyDeck((prev) => [...prev, returnedCard]);

        // レストSPをアクティブに
        if (myRestedSP > 0) {
          setMyRestedSP((prev) => prev - 1);
          setMyActiveSP((prev) => prev + 1);
          addLog(`${returnedCard.name}をデッキに戻し、レストSP1個をアクティブにした`, 'info');
        }
      },
    },
  ],

  /**
   * C0000379: 虹羽密林の青滴虫・サルフィス
   * 【召喚時】自分のデッキの上から1枚を墓地に送る。墓地に送ったカードが《虹羽密林》モンスターだった場合、自分のSPトークンを1個アクティブにする。
   */
  C0000379: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 1枚ミル、《虹羽密林》ならSP回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        const milledCards = millDeck(context as any, 1);

        if (milledCards.length > 0) {
          const card = milledCards[0];
          if (card.name && card.name.includes('虹羽密林')) {
            if (myRestedSP > 0) {
              setMyRestedSP((prev) => prev - 1);
              setMyActiveSP((prev) => prev + 1);
              addLog('虹羽密林の青滴虫・サルフィスの効果: レストSP1個をアクティブにした', 'info');
            }
          }
        }
      },
    },
  ],

  /**
   * C0000380: 虹羽密林の湖鱗獣・アクアレオン
   * 【常時】このカードの攻撃力は、自分の場の《虹羽密林》モンスター1体につき500アップ。
   */
  C0000380: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 《虹羽密林》1体につき攻撃力+500',
      effect: (context: TriggerContext) => {
        const { monsterIndex } = context;
        const { myField } = getPlayerContext(context);

        // 場の《虹羽密林》をカウント（自分自身を除く）
        const rainbowFeatherCount = myField.filter((monster, idx) =>
          monster &&
          idx !== monsterIndex &&
          monster.name &&
          monster.name.includes('虹羽密林')
        ).length;

        if (rainbowFeatherCount > 0) {
          const attackBonus = rainbowFeatherCount * 500;
          modifyAttack(context as any, attackBonus, monsterIndex, false, true);
        }
      },
    },
  ],

  /**
   * C0000399: 呪術狩りの鎖術師ミリア
   * 【常時】相手の闇属性モンスターの攻撃力をターン終了時まで300ダウン。
   */
  C0000399: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 相手の闇属性モンスターの攻撃力-300',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('呪術狩りの鎖術師ミリアの常時効果: 相手の闇属性攻撃力-300（未実装）', 'info');
        // TODO: フィールドカードの常時効果システムが必要
      },
    },
  ],

  /**
   * C0000403: 沼の血叫びヴォルガノス
   * 【召喚時】相手プレイヤーに600ダメージを与える。
   */
  C0000403: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手に600ダメージ',
      effect: (context: TriggerContext) => {
        context.addLog('沼の血叫びヴォルガノスの効果: 相手に600ダメージ', 'damage');
        conditionalDamage(context as any, 600, 'opponent');
      },
    },
  ],

  /**
   * C0000421: 沼の狩人クルーロプス
   * 【召喚時】1枚ドローする。
   */
  C0000421: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 1枚ドロー',
      effect: (context: TriggerContext) => {
        context.addLog('沼の狩人クルーロプスの効果: 1枚ドロー', 'info');
        drawCards(context as any, 1);
      },
    },
  ],

  /**
   * C0000422: 黒涙の山椒魚サラマグナ
   * 【自壊時】自分の墓地の水属性カード1枚を手札に戻す。
   */
  C0000422: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 墓地の水属性カード1枚を手札に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyHand } = getPlayerContext(context);

        // 墓地から水属性カードを探す
        const targetCard = myGraveyard.find((card) => card.attribute === '水');

        if (!targetCard) {
          addLog('黒涙の山椒魚サラマグナの効果: 墓地に水属性カードがありません', 'info');
          return;
        }

        // 墓地から手札に戻す
        setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));
        setMyHand((prev) => [...prev, targetCard]);
        addLog(`${targetCard.name}を手札に戻した`, 'info');
      },
    },
  ],

  /**
   * C0000423: 黒涙の毒樹フロラクルス
   * 【相手モンスター攻撃時】その攻撃力をターン終了時まで300ダウン。
   */
  C0000423: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手モンスター攻撃時: 攻撃力-300',
      effect: (context: TriggerContext) => {
        const { attacker, attackerIndex, addLog } = context;
        // currentPlayerは被攻撃者視点なので、相手フィールドを操作
        const { opponentField, setOpponentField } = getPlayerContext(context);

        if (!attacker || attackerIndex === undefined) {
          return;
        }

        // 攻撃モンスターの攻撃力を300ダウン（ターン終了まで）
        setOpponentField(prev => {
          const newField = [...prev];
          if (newField[attackerIndex]) {
            const monster = newField[attackerIndex]!;
            newField[attackerIndex] = applyTemporaryAttackReduction(monster, 300);
          }
          return newField;
        });

        addLog(`黒涙の毒樹フロラクルスの効果: ${attacker.name}の攻撃力-300！`, 'damage');
      },
    },
  ],

  /**
   * C0000284: 白蛇の牙
   * 【毒侵】このカードが相手プレイヤーにダメージを与えた時、相手を「毒」状態（毎ターン終了時100ダメージ）にする。
   */
  C0000284: [
    {
      type: TRIGGER_TYPES.ON_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手プレイヤーへの直接攻撃成功時: 相手を毒状態に',
      effect: (context: TriggerContext) => {
        const { addLog, targetType, setOpponentPlayerStatus } = context;
        const { setOpponentLife, opponentLife } = getPlayerContext(context);

        // 直接攻撃（プレイヤーへの攻撃）かどうか確認
        if (targetType !== 'player') {
          return; // モンスターへの攻撃は対象外
        }

        // プレイヤーに毒状態を付与
        // Note: プレイヤー状態異常はゲーム状態で管理する必要があるが
        // 現在は簡易的にログ表示とターン終了時にダメージを与える仕組みで代用
        // playerStatusをcontext経由で受け取って設定する
        if (setOpponentPlayerStatus) {
          setOpponentPlayerStatus((prev: any) => ({
            ...prev,
            poison: {
              active: true,
              damage: 100,
              source: 'C0000284',
              sourceName: '白蛇の牙',
            },
          }));
        }

        addLog(`白蛇の牙の【毒侵】: 相手プレイヤーを毒状態にした！（毎ターン終了時100ダメージ）`, 'damage');
      },
    },
  ],

  // ========================================
  // ガーディアンモンスター
  // ========================================

  /**
   * C0000436: フロストウォール・ガーディアン
   * 【召喚時】相手モンスター1体を「凍結（攻撃力半減＋行動不能）」にする（次のターン開始時に50%で解除）。
   */
  C0000436: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体を凍結状態に',
      effect: (context: TriggerContext) => {
        // 凍結: 攻撃力半減 + 行動不能、ターン開始時50%解除
        selectAndApplyStatusToOpponent(context as any,
          STATUS_EFFECT_TYPES.FREEZE,
          {
            duration: -1, // 解除されるまで継続
            removeChance: 0.5, // 50%で解除
          },
          'フロストウォール・ガーディアン'
        );
      },
    },
  ],

  /**
   * C0000445: マーメイドクイーン
   * 【召喚時】1枚ドローする。
   * 【常時】［マーメイド］が召喚された時、そのカードのHPを600アップする。（monsterCards.jsで実装）
   */
  C0000445: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 1枚ドロー',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('マーメイドクイーンの召喚時効果: 1枚ドロー', 'info');
        drawCards(context as any, 1);
      },
    },
  ],

  // =============================================================================
  // 深海領域システム - 水属性カード
  // =============================================================================

  /**
   * C0000522: 荒海の斥候・スカウトフィン
   * 【召喚時/浮上時】自分のデッキの上から3枚を見て、水属性カード1枚を手札に加えてもよい。残りをデッキの下に戻す。
   */
  C0000522: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: デッキ上3枚から水属性1枚を手札に',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('スカウトフィンの効果: デッキにカードがありません', 'info');
          return;
        }

        const topCards = myDeck.slice(0, Math.min(3, myDeck.length));
        const waterCards = topCards.filter((c: CardWithId) => c.attribute === '水');

        if (waterCards.length === 0) {
          addLog('スカウトフィンの効果: 水属性カードがなかった（デッキの下に戻す）', 'info');
          setMyDeck((prev: CardWithId[]) => {
            const rest = prev.slice(topCards.length);
            return [...rest, ...topCards];
          });
          return;
        }

        // 選択UIを表示（水属性カードのみ選択可能）
        if (setPendingDeckReview) {
          setPendingDeckReview({
            cards: waterCards, // 水属性カードのみ表示
            count: 1,
            message: 'スカウトフィンの効果: 水属性カード1枚を手札に加えてもよい（キャンセルで加えない）',
            allowCancel: true,
            callback: (selectedCards: any[]) => {
              if (selectedCards.length > 0) {
                const selectedCard = selectedCards[0] as CardWithId;
                const remainingCards = topCards.filter((c: CardWithId) => c.uniqueId !== selectedCard.uniqueId);
                addLog(`スカウトフィンの効果: ${selectedCard.name}を手札に加えた`, 'info');
                setMyHand((prev: CardWithId[]) => [...prev, selectedCard]);
                setMyDeck((prev: CardWithId[]) => {
                  const rest = prev.slice(topCards.length);
                  return [...rest, ...remainingCards];
                });
              } else {
                // キャンセル時：全てデッキの下に戻す
                addLog('スカウトフィンの効果: カードを加えなかった（デッキの下に戻す）', 'info');
                setMyDeck((prev: CardWithId[]) => {
                  const rest = prev.slice(topCards.length);
                  return [...rest, ...topCards];
                });
              }
            },
          });
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_SURFACE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '浮上時: デッキ上3枚から水属性1枚を手札に',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('スカウトフィンの浮上時効果: デッキにカードがありません', 'info');
          return;
        }

        const topCards = myDeck.slice(0, Math.min(3, myDeck.length));
        const waterCards = topCards.filter((c: CardWithId) => c.attribute === '水');

        if (waterCards.length === 0) {
          addLog('スカウトフィンの浮上時効果: 水属性カードがなかった（デッキの下に戻す）', 'info');
          setMyDeck((prev: CardWithId[]) => {
            const rest = prev.slice(topCards.length);
            return [...rest, ...topCards];
          });
          return;
        }

        // 選択UIを表示（水属性カードのみ選択可能）
        if (setPendingDeckReview) {
          setPendingDeckReview({
            cards: waterCards, // 水属性カードのみ表示
            count: 1,
            message: 'スカウトフィンの浮上時効果: 水属性カード1枚を手札に加えてもよい（キャンセルで加えない）',
            allowCancel: true,
            callback: (selectedCards: any[]) => {
              if (selectedCards.length > 0) {
                const selectedCard = selectedCards[0] as CardWithId;
                const remainingCards = topCards.filter((c: CardWithId) => c.uniqueId !== selectedCard.uniqueId);
                addLog(`スカウトフィンの浮上時効果: ${selectedCard.name}を手札に加えた`, 'info');
                setMyHand((prev: CardWithId[]) => [...prev, selectedCard]);
                setMyDeck((prev: CardWithId[]) => {
                  const rest = prev.slice(topCards.length);
                  return [...rest, ...remainingCards];
                });
              } else {
                // キャンセル時：全てデッキの下に戻す
                addLog('スカウトフィンの浮上時効果: カードを加えなかった（デッキの下に戻す）', 'info');
                setMyDeck((prev: CardWithId[]) => {
                  const rest = prev.slice(topCards.length);
                  return [...rest, ...topCards];
                });
              }
            },
          });
        }
      },
    },
  ],

  /**
   * C0000523: 荒海の待ち伏せ・アンブッシャー
   * 【浮上時】深度3以上で浮上した時、相手モンスター1体に深度×200ダメージ
   */
  C0000523: [
    {
      type: TRIGGER_TYPES.ON_SURFACE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '浮上時: 深度3以上で相手モンスターに深度×200ダメージ',
      condition: (context: TriggerContext) => {
        const surfaceDepth = (context as any).surfaceDepth || 0;
        return surfaceDepth >= 3;
      },
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const surfaceDepth = (context as any).surfaceDepth || 3;
        const damage = calculateShihenNoKibaDamage(surfaceDepth);
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        const validTargets = opponentField
          .map((m: MonsterInstance | null, idx: number) => ({ monster: m, index: idx }))
          .filter(({ monster }: { monster: MonsterInstance | null }) => monster !== null);

        if (validTargets.length === 0) {
          addLog('アンブッシャーの効果: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        const applyDamage = (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          const newHp = (target.currentHp || target.hp || 0) - damage;
          addLog(`アンブッシャーの効果: ${target.name}に${damage}ダメージ（深度${surfaceDepth}）`, 'damage');

          if (newHp <= 0) {
            addLog(`${target.name}が破壊された！`, 'damage');
            setOpponentGraveyard((prev: CardWithId[]) => [...prev, target]);
            setOpponentField((prev: (MonsterInstance | null)[]) => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
          } else {
            setOpponentField((prev: (MonsterInstance | null)[]) => {
              const newField = [...prev];
              newField[targetIndex] = { ...target, currentHp: newHp };
              return newField;
            });
          }
        };

        if (validTargets.length === 1) {
          applyDamage(validTargets[0].index);
        } else if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: `アンブッシャー: ${damage}ダメージを与える相手モンスターを選択`,
            targetPlayer: 'opponent',
            callback: (selectedIndex: number) => applyDamage(selectedIndex),
          });
        } else {
          applyDamage(validTargets[0].index);
        }
      },
    },
  ],

  /**
   * C0000524: 荒海の番人・アビスガード
   * 【海底の眠り】深海領域中LP回復 (処理はエンドフェイズ深度加算時に別途)
   * 【浮上時】自分の水属性モンスター1体に守護を付与する
   */
  C0000524: [
    {
      type: TRIGGER_TYPES.ON_SURFACE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '浮上時: 自分の水属性モンスター1体に守護付与',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField } = getPlayerContext(context);

        const waterMonsters = myField
          .map((m: MonsterInstance | null, idx: number) => ({ monster: m, index: idx }))
          .filter(({ monster }: { monster: MonsterInstance | null }) =>
            monster !== null && monster.attribute === '水'
          );

        if (waterMonsters.length === 0) {
          addLog('アビスガードの効果: 対象となる水属性モンスターがいません', 'info');
          return;
        }

        const applyGuard = (targetIndex: number) => {
          setMyField((prev: (MonsterInstance | null)[]) => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (target) {
              const effects = target.statusEffects || [];
              newField[targetIndex] = {
                ...target,
                statusEffects: [
                  ...effects,
                  {
                    type: STATUS_EFFECT_TYPES.GUARD,
                    duration: -1,
                    source: 'C0000524',
                    sourceName: 'アビスガード',
                  },
                ],
              };
              addLog(`アビスガードの効果: ${target.name}に守護を付与`, 'info');
            }
            return newField;
          });
        };

        if (waterMonsters.length === 1) {
          applyGuard(waterMonsters[0].index);
        } else if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: 'アビスガード: 守護を付与する水属性モンスターを選択',
            targetPlayer: 'self',
            callback: (selectedIndex: number) => applyGuard(selectedIndex),
          });
        } else {
          applyGuard(waterMonsters[0].index);
        }
      },
    },
  ],

  /**
   * C0000526: ヴェルゼファールの眷属・レヴィアタン
   * 【魂結】: 召喚時、場にいる別の《ヴェルゼファール》モンスター1体とリンクし、双方の攻撃力を400アップ。
   * 【浮上時】深度3以上で浮上した時、相手モンスター全体に深度×200ダメージ。深度5以上なら、追加で相手のフィールド/フェイズカードを1枚破壊。
   */
  C0000526: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '【魂結】: 『ヴェルゼファール』とリンクし双方ATK+400',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField } = getPlayerContext(context);

        const effect = getKonketsuEffect('C0000526');
        if (!effect) return;

        const sourceMonster = myField[monsterIndex ?? -1];
        if (!sourceMonster) return;

        const targets = findLinkableTargets(myField, effect.targetCondition, sourceMonster.uniqueId);

        if (targets.length === 0) {
          addLog('【魂結】リンク対象の『ヴェルゼファール』モンスターがいません', 'info');
          return;
        }

        if (targets.length === 1) {
          executeKonketsuLink(
            sourceMonster, monsterIndex!,
            targets[0].monster, targets[0].slotIndex,
            effect, setMyField as any, addLog as any
          );
          return;
        }

        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '【魂結】リンク対象を選択してください',
            targetPlayer: 'self',
            validIndices: targets.map(t => t.slotIndex),
            callback: (selectedIndex: number) => {
              const target = targets.find(t => t.slotIndex === selectedIndex);
              if (target) {
                executeKonketsuLink(
                  sourceMonster, monsterIndex!,
                  target.monster, target.slotIndex,
                  effect, setMyField as any, addLog as any
                );
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_SURFACE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '浮上時: 深度3以上で相手全体に深度×200ダメージ、深度5+でカード破壊',
      condition: (context: TriggerContext) => {
        const surfaceDepth = (context as any).surfaceDepth || 0;
        return surfaceDepth >= 3;
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const surfaceDepth = (context as any).surfaceDepth || 3;
        const damage = calculateShihenNoKibaDamage(surfaceDepth);
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        addLog(`レヴィアタンの浮上時効果: 相手全体に${damage}ダメージ（深度${surfaceDepth}）`, 'damage');

        setOpponentField((prev: (MonsterInstance | null)[]) => {
          const newField: (MonsterInstance | null)[] = [];
          prev.forEach((monster: MonsterInstance | null) => {
            if (monster) {
              const newHp = (monster.currentHp || monster.hp || 0) - damage;
              if (newHp <= 0) {
                addLog(`${monster.name}が破壊された！`, 'damage');
                setOpponentGraveyard((g: CardWithId[]) => [...g, monster]);
                newField.push(null);
              } else {
                newField.push({ ...monster, currentHp: newHp });
              }
            } else {
              newField.push(null);
            }
          });
          return newField;
        });

        // 深度5以上で追加効果
        if (surfaceDepth >= 5) {
          // 相手フィールドカードまたはフェイズカードを破壊
          const opponentPlayer = context.currentPlayer === 1 ? 2 : 1;
          const opponentFieldCard = opponentPlayer === 1
            ? (context as any).p1FieldCard
            : (context as any).p2FieldCard;
          const opponentPhaseCard = opponentPlayer === 1
            ? (context as any).p1PhaseCard
            : (context as any).p2PhaseCard;

          if (opponentFieldCard) {
            const setOpponentFieldCard = opponentPlayer === 1
              ? (context as any).setP1FieldCard
              : (context as any).setP2FieldCard;
            if (setOpponentFieldCard) {
              setOpponentFieldCard(null);
              addLog(`レヴィアタンの効果: 相手のフィールドカードを破壊！`, 'damage');
            }
          } else if (opponentPhaseCard) {
            const setOpponentPhaseCard = opponentPlayer === 1
              ? (context as any).setP1PhaseCard
              : (context as any).setP2PhaseCard;
            if (setOpponentPhaseCard) {
              setOpponentPhaseCard(null);
              addLog(`レヴィアタンの効果: 相手のフェイズカードを破壊！`, 'damage');
            }
          } else {
            addLog(`レヴィアタンの効果: 破壊対象のカードがありません`, 'info');
          }
        }
      },
    },
  ],

  /**
   * C0000527: 荒海王ヴェルゼファール
   * 【潜航時】このカードは深度+2で深海領域に入る
   * 【浮上時】深度3以上で浮上した時、相手モンスター1体破壊
   * 【常時】このカードが場にいる間、他の［ディープシャドウ］のATK+400 → monsterCards.ts で実装
   * 基本技: 深海領域の全カードに深度+1 → cardEffects/water.ts で実装
   */
  C0000527: [
    {
      type: TRIGGER_TYPES.ON_SUBMERGE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '潜航時: 深度+2で開始',
      effect: (context: TriggerContext) => {
        // 初期深度を2に設定（この効果はsubmergeCard呼び出し時にinitialDepth: 2として処理）
        const { addLog } = context;
        addLog('ヴェルゼファールは深度2で深海領域に入った！', 'info');
        // Note: 実際の深度+2はGameActionsのSUBMERGE_CARD呼び出し時にinitialDepth: 2を渡す形で実装
      },
    },
    {
      type: TRIGGER_TYPES.ON_SURFACE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '浮上時: 深度3以上で相手モンスター1体破壊',
      condition: (context: TriggerContext) => {
        const surfaceDepth = (context as any).surfaceDepth || 0;
        return surfaceDepth >= 3;
      },
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        const validTargets = opponentField
          .map((m: MonsterInstance | null, idx: number) => ({ monster: m, index: idx }))
          .filter(({ monster }: { monster: MonsterInstance | null }) => monster !== null);

        if (validTargets.length === 0) {
          addLog('ヴェルゼファールの効果: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        const destroyMonsterAtIndex = (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          addLog(`ヴェルゼファールの効果: ${target.name}を破壊！`, 'damage');
          setOpponentGraveyard((prev: CardWithId[]) => [...prev, target]);
          setOpponentField((prev: (MonsterInstance | null)[]) => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
        };

        if (validTargets.length === 1) {
          destroyMonsterAtIndex(validTargets[0].index);
        } else if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: 'ヴェルゼファール: 破壊する相手モンスターを選択',
            targetPlayer: 'opponent',
            callback: (selectedIndex: number) => destroyMonsterAtIndex(selectedIndex),
          });
        } else {
          destroyMonsterAtIndex(validTargets[0].index);
        }
      },
    },
  ],
};

/**
 * 水属性カードがトリガー実装を持っているかチェック
 * @param {string} cardId - カードID
 * @returns {boolean} トリガー実装を持っている場合true
 */
export const hasWaterCardTrigger = (cardId: string): boolean => {
  const triggers = waterCardTriggers as Record<string, unknown>;
  return cardId !== undefined && cardId !== null && triggers[cardId] !== undefined;
};

/**
 * 水属性カードのトリガーを取得
 * @param {string} cardId - カードID
 * @returns {Array|null} トリガー配列、または null
 */
export const getWaterCardTriggers = (cardId: string): TriggerDefinition[] | null => {
  const triggers = waterCardTriggers as unknown as Record<string, TriggerDefinition[]>;
  if (!cardId || !triggers[cardId]) {
    return null;
  }
  return triggers[cardId];
};

/**
 * 実装済み水属性カード数を取得
 * @returns {number} 実装済みカード数
 */
export const getWaterCardCount = () => {
  return Object.keys(waterCardTriggers).length;
};
